import React, { useMemo, useState } from 'react';
import { Alert, Chip, TextField, Tooltip } from '@mui/material';
import { AlertTriangle, Braces, Layers, Scissors, Search } from 'lucide-react';
import styles from './KgInspectPanel.module.scss';
import { coverageTone, formatCoverage, formatNumber, truncateValue } from './kgUtils';

/** Long enough to recognise a value, short enough to keep the row one line. */
const EXAMPLES_SHOWN = 3;

/**
 * The result of POST /kg-datasets/inspect, rendered.
 *
 * This is the step that comes BEFORE the mapping — the first honest look at what
 * is actually in the customer's export. It matters because the answer is
 * routinely surprising: an event feed where the interesting fields are inside a
 * JSON string, half the columns empty in every record, a column name with a dot
 * in it that would silently resolve to nothing.
 *
 * Two things are rendered rather than merely received:
 *
 *   • truncation — coverage describes what was INSPECTED. Reading "88%" as a
 *     property of the whole dataset is exactly the floor-as-total mistake the
 *     rest of this feature is careful about.
 *   • preprocessing — when the server decoded or derived anything, the paths
 *     below only exist because of it. They are a pair, and saying so is what
 *     stops someone hand-copying `normalized.event_id` into a config that has no
 *     normalization block.
 */
const KgInspectPanel = ({ inspection, compact = false }) => {
  const [query, setQuery] = useState('');

  // Memoised so the sort below isn't re-run on every keystroke in the filter:
  // `inspection?.fields || []` is a fresh array each render otherwise.
  const fields = useMemo(() => inspection?.fields || [], [inspection]);
  const preprocessing = inspection?.preprocessing || null;
  const decode = Array.isArray(preprocessing?.decode) ? preprocessing.decode : [];
  const derived = Array.isArray(preprocessing?.fields) ? preprocessing.fields : [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? fields.filter((f) => f.path.toLowerCase().includes(q)) : fields;
    // Populated columns first: an empty one is worth seeing, but never worth
    // scrolling past forty of them to reach the field you came for.
    return [...list].sort((a, b) => b.coveragePct - a.coveragePct || a.path.localeCompare(b.path));
  }, [fields, query]);

  if (!inspection) return null;

  const emptyCount = inspection.emptyFields?.length || 0;
  const quotedCount = inspection.quotedFields?.length || 0;

  return (
    <div className={styles.panel}>
      {/* ── What was actually looked at ── */}
      <div className={styles.summaryRow}>
        {/* Only when the server actually reported a count — "0 records
            inspected" above a full column list reads as a bug. */}
        {inspection.inspectedCount > 0 && (
          <span className={styles.summaryChip}>
            <strong>{formatNumber(inspection.inspectedCount)}</strong> records inspected
          </span>
        )}
        <span className={styles.summaryChip}>
          <strong>{formatNumber(fields.length)}</strong> columns found
        </span>
        {emptyCount > 0 && (
          <Tooltip title="Empty in every record inspected. Mapping a facet to one of these gives a facet at 0% coverage.">
            <span className={`${styles.summaryChip} ${styles.warn}`}>
              <strong>{formatNumber(emptyCount)}</strong> always empty
            </span>
          </Tooltip>
        )}
        {inspection.derivedFields?.length > 0 && (
          <span className={`${styles.summaryChip} ${styles.derived}`}>
            <Layers size={12} />
            <strong>{formatNumber(inspection.derivedFields.length)}</strong> derived
          </span>
        )}
      </div>

      {/* Coverage below is a property of the sample, not of the dataset. */}
      {inspection.truncated && (
        <Alert severity="info" icon={<Scissors size={16} />} className={styles.alert}>
          Only part of what you supplied was inspected — the endpoint caps at 200 records and
          2&nbsp;MB. Everything below describes those records, so treat a coverage figure as “in
          the sample”, not “in your data”.
        </Alert>
      )}

      {/* ── What the server had to do to read the file ── */}
      {(decode.length > 0 || derived.length > 0) && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <Braces size={14} />
            Input normalization was applied
          </h4>
          <p className={styles.sectionHelp}>
            Your records aren’t flat, so some values were unpacked before anything else could see
            them. The originals are untouched — the results are added alongside, under{' '}
            <code>{preprocessing?.namespace || 'normalized'}</code>. This config is saved with the
            mapping, because the column paths below only exist while it does.
          </p>
          {decode.length > 0 && (
            <div className={styles.preRow}>
              <span className={styles.preLabel}>Decoded JSON</span>
              <div className={styles.preChips}>
                {decode.map((entry, i) => (
                  <Chip
                    key={`${entry.path}-${i}`}
                    size="small"
                    className={styles.preChip}
                    label={`${entry.path} → ${entry.target}`}
                  />
                ))}
              </div>
            </div>
          )}
          {derived.length > 0 && (
            <div className={styles.preRow}>
              <span className={styles.preLabel}>Derived fields</span>
              <div className={styles.preChips}>
                {derived.map((field, i) => (
                  <Tooltip
                    key={`${field.name}-${i}`}
                    title={
                      (field.candidates || []).length > 1
                        ? `Tries, in order: ${(field.candidates || [])
                            .map((c) => c.path)
                            .join(' → ')}`
                        : `From ${(field.candidates || [])[0]?.path || 'the record'}`
                    }
                  >
                    <Chip
                      size="small"
                      className={styles.preChip}
                      label={`${preprocessing?.namespace || 'normalized'}.${field.name}`}
                    />
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Records that couldn't be read at all ── */}
      {inspection.failures.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <AlertTriangle size={14} />
            {formatNumber(inspection.failures.length)} record
            {inspection.failures.length === 1 ? '' : 's'} could not be read
          </h4>
          <ul className={styles.plainList}>
            {inspection.failures.slice(0, compact ? 3 : 10).map((failure, i) => (
              <li key={i}>
                {typeof failure === 'string' ? (
                  failure
                ) : (
                  <>
                    {failure.what && <code>{failure.what}</code>}{' '}
                    {failure.why || failure.error || JSON.stringify(failure)}
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {inspection.warnings.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <AlertTriangle size={14} />
            Warnings
          </h4>
          <ul className={styles.plainList}>
            {inspection.warnings.map((warning, i) => (
              <li key={i}>{String(warning)}</li>
            ))}
          </ul>
        </section>
      )}

      {/* ── The columns ── */}
      <section className={styles.section}>
        <div className={styles.fieldsHead}>
          <h4 className={styles.sectionTitle}>Columns in your data</h4>
          {fields.length > 8 && (
            <TextField
              size="small"
              placeholder="Filter columns"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 6 }} /> }}
              className={styles.fieldsSearch}
            />
          )}
        </div>

        {quotedCount > 0 && (
          <p className={styles.sectionHelp}>
            {quotedCount} column name{quotedCount === 1 ? '' : 's'} contained a space, hyphen or
            dot and {quotedCount === 1 ? 'is' : 'are'} shown wrapped in backticks. That is
            deliberate: without it <code>tbl.col</code> reads as “field tbl, then field col” and
            matches nothing.
          </p>
        )}

        <div className={styles.table}>
          <div className={`${styles.tableRow} ${styles.tableHead}`}>
            <span>Column</span>
            <span>Type</span>
            <span>Coverage</span>
            <span>Distinct</span>
            <span>Examples</span>
          </div>
          {filtered.length === 0 ? (
            <div className={styles.tableEmpty}>No column matches “{query}”.</div>
          ) : (
            filtered.slice(0, compact ? 12 : 400).map((field) => (
              <div key={field.path} className={styles.tableRow}>
                <span className={styles.cellPath}>
                  <code>{field.path}</code>
                  {field.derived && (
                    <Tooltip title="Produced by input normalization — this column does not exist in your raw file.">
                      <em className={styles.tagDerived}>derived</em>
                    </Tooltip>
                  )}
                  {field.multiValued && (
                    <Tooltip title="Several values per record. A facet on this needs cardinality “many”.">
                      <em className={styles.tagMulti}>many</em>
                    </Tooltip>
                  )}
                </span>
                <span className={styles.cellType}>{field.type || '—'}</span>
                <span
                  className={`${styles.cellCoverage} ${styles[coverageTone(field.coveragePct)]}`}
                >
                  {formatCoverage(field.coveragePct)}
                </span>
                <span className={styles.cellDistinct}>
                  {field.distinct
                    ? `${formatNumber(field.distinct)}${field.distinctTruncated ? '+' : ''}`
                    : '—'}
                </span>
                <span className={styles.cellExamples}>
                  {field.values.length === 0 ? (
                    <em>empty in every record</em>
                  ) : (
                    field.values.slice(0, EXAMPLES_SHOWN).map((value, i) => (
                      <code key={i} className={styles.exampleChip}>
                        {truncateValue(
                          typeof value === 'object' ? JSON.stringify(value) : value,
                          38
                        )}
                      </code>
                    ))
                  )}
                </span>
              </div>
            ))
          )}
        </div>
        {filtered.length > 400 && (
          <p className={styles.sectionHelp}>
            Showing the first 400 of {formatNumber(filtered.length)} columns — filter to narrow
            it down.
          </p>
        )}
      </section>
    </div>
  );
};

export default KgInspectPanel;
